import { useEffect, useState } from "react";
import { useGT } from "gt-react";
import { AppWindow, Copy, Download, Server, Terminal, X } from "lucide-react";
import { useAppStore } from "../store";
import { toast } from "./Toasts";

const DESKTOP_RELEASES_URL = "https://github.com/sebastian-breitzke/spuerhund/releases";

interface ReleaseAsset {
  name: string;
  browser_download_url: string;
}

interface DesktopRelease {
  version: string;
  assets: ReleaseAsset[];
  html_url: string;
}

function findAssetByPattern(assets: ReleaseAsset[], pattern: RegExp): string | null {
  return assets.find((a) => pattern.test(a.name))?.browser_download_url ?? null;
}

async function loadDesktopRelease(): Promise<DesktopRelease | null> {
  try {
    const res = await fetch(
      "https://api.github.com/repos/sebastian-breitzke/spuerhund/releases?per_page=20",
    );
    if (!res.ok) return null;
    const releases = (await res.json()) as Array<{
      tag_name?: string;
      assets: ReleaseAsset[];
      html_url: string;
    }>;
    const desktop = releases.find((r) => r.tag_name?.startsWith("desktop-v"));
    if (!desktop) return null;
    return {
      version: desktop.tag_name!.replace("desktop-v", ""),
      assets: desktop.assets,
      html_url: desktop.html_url,
    };
  } catch {
    return null;
  }
}

export function LocalAppsDialog() {
  const t = useGT();
  const show = useAppStore((s) => s.showLocalAppsDialog);
  const setShow = useAppStore((s) => s.setShowLocalAppsDialog);
  const setShowRuntimeDialog = useAppStore((s) => s.setShowRuntimeDialog);
  const [release, setRelease] = useState<DesktopRelease | null>(null);

  useEffect(() => {
    if (show) void loadDesktopRelease().then(setRelease);
  }, [show]);

  if (!show) return null;

  const dmgUrl = release ? findAssetByPattern(release.assets, /\.dmg$/) : null;
  const exeUrl = release ? findAssetByPattern(release.assets, /\.exe$/) : null;
  const appImageUrl = release ? findAssetByPattern(release.assets, /\.AppImage$/) : null;

  const handleCopy = async (command: string) => {
    await navigator.clipboard.writeText(command);
    toast(t("Command copied"), "success");
  };

  const handlePairRuntime = () => {
    setShow(false);
    setShowRuntimeDialog(true);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && setShow(false)}
    >
      <div className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl border border-border bg-surface-raised shadow-2xl">
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <div className="flex items-center gap-3">
            <AppWindow size={14} className="text-zinc-400" />
            <div>
              <h2 className="text-sm font-semibold text-zinc-200">{t("Local Apps")}</h2>
              <p className="mt-0.5 text-[11px] text-zinc-500">
                {t("Desktop app, local runtime, and CLI for agents")}
              </p>
            </div>
          </div>
          <button
            onClick={() => setShow(false)}
            className="rounded p-1 text-zinc-500 hover:bg-surface hover:text-zinc-300"
            title={t("Close")}
          >
            <X size={14} />
          </button>
        </div>

        <div className="space-y-4 overflow-y-auto px-5 py-5">
          {/* Card 1: Desktop App */}
          <div className="rounded-xl border border-border bg-surface px-4 py-4">
            <div className="mb-3 flex items-center gap-2">
              <AppWindow size={13} className="text-brand" />
              <h3 className="text-xs font-semibold uppercase tracking-wide text-zinc-300">
                {t("Desktop App")}
              </h3>
            </div>
            <p className="mb-3 text-xs leading-5 text-zinc-500">
              {t("Native Mac, Windows, and Linux app. Everything in one package — UI, runtime, and CLI.")}
            </p>

            {/* macOS via Homebrew Cask */}
            <div className="mb-2 rounded-lg border border-border bg-surface-raised px-3 py-3">
              <div className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-zinc-500">
                {t("macOS · Homebrew")}
              </div>
              <div className="flex items-center justify-between gap-2 rounded bg-surface px-2.5 py-2 font-mono text-xs text-zinc-100 ring-1 ring-border">
                <span className="truncate">brew install --cask sebastian-breitzke/tap/spuerhund-app</span>
                <button
                  type="button"
                  onClick={() => void handleCopy("brew install --cask sebastian-breitzke/tap/spuerhund-app")}
                  className="rounded p-1 text-zinc-500 hover:bg-surface-raised hover:text-zinc-200"
                  title={t("Copy command")}
                >
                  <Copy size={12} />
                </button>
              </div>
            </div>

            {/* Direct downloads */}
            <div className="flex flex-wrap gap-2">
              <a
                href={dmgUrl ?? DESKTOP_RELEASES_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 rounded border border-border bg-surface-raised px-2.5 py-1.5 text-[11px] font-medium text-zinc-300 hover:bg-surface hover:text-zinc-100"
              >
                <Download size={11} />
                {t("macOS")} .dmg
              </a>
              <a
                href={exeUrl ?? DESKTOP_RELEASES_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 rounded border border-border bg-surface-raised px-2.5 py-1.5 text-[11px] font-medium text-zinc-300 hover:bg-surface hover:text-zinc-100"
              >
                <Download size={11} />
                {t("Windows")} .exe
              </a>
              <a
                href={appImageUrl ?? DESKTOP_RELEASES_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 rounded border border-border bg-surface-raised px-2.5 py-1.5 text-[11px] font-medium text-zinc-300 hover:bg-surface hover:text-zinc-100"
              >
                <Download size={11} />
                {t("Linux")} .AppImage
              </a>
            </div>
            {release && (
              <div className="mt-2 text-[10px] text-zinc-600">
                v{release.version} · <a href={release.html_url} target="_blank" rel="noopener noreferrer" className="hover:text-zinc-400">{t("Release notes")}</a>
              </div>
            )}
          </div>

          {/* Card 2: Local Runtime */}
          <div className="rounded-xl border border-border bg-surface px-4 py-4">
            <div className="mb-3 flex items-center gap-2">
              <Server size={13} className="text-accent" />
              <h3 className="text-xs font-semibold uppercase tracking-wide text-zinc-300">
                {t("Local Runtime")}
              </h3>
            </div>
            <p className="mb-3 text-xs leading-5 text-zinc-500">
              {t("Runs on your machine so this browser app can execute queries against localhost APIs, Mongo, and Postgres without CORS.")}
            </p>

            <div className="mb-2 rounded-lg border border-border bg-surface-raised px-3 py-3">
              <div className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-zinc-500">
                {t("macOS · Homebrew")}
              </div>
              <div className="flex items-center justify-between gap-2 rounded bg-surface px-2.5 py-2 font-mono text-xs text-zinc-100 ring-1 ring-border">
                <span className="truncate">brew install sebastian-breitzke/tap/spuerhund</span>
                <button
                  type="button"
                  onClick={() => void handleCopy("brew install sebastian-breitzke/tap/spuerhund")}
                  className="rounded p-1 text-zinc-500 hover:bg-surface-raised hover:text-zinc-200"
                  title={t("Copy command")}
                >
                  <Copy size={12} />
                </button>
              </div>
            </div>

            <div className="mb-3 rounded-lg border border-border bg-surface-raised px-3 py-3">
              <div className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-zinc-500">
                {t("Windows, Linux · npx")}
              </div>
              <div className="flex items-center justify-between gap-2 rounded bg-surface px-2.5 py-2 font-mono text-xs text-zinc-100 ring-1 ring-border">
                <span className="truncate">npx spuerhund runtime serve</span>
                <button
                  type="button"
                  onClick={() => void handleCopy("npx spuerhund runtime serve")}
                  className="rounded p-1 text-zinc-500 hover:bg-surface-raised hover:text-zinc-200"
                  title={t("Copy command")}
                >
                  <Copy size={12} />
                </button>
              </div>
            </div>

            <button
              type="button"
              onClick={handlePairRuntime}
              className="text-[11px] text-accent-light hover:text-accent hover:underline"
            >
              {t("Pair this browser with your runtime →")}
            </button>
          </div>

          {/* Card 3: CLI */}
          <div className="rounded-xl border border-border bg-surface px-4 py-4">
            <div className="mb-3 flex items-center gap-2">
              <Terminal size={13} className="text-success" />
              <h3 className="text-xs font-semibold uppercase tracking-wide text-zinc-300">
                {t("CLI for agents and scripts")}
              </h3>
            </div>
            <p className="mb-3 text-xs leading-5 text-zinc-500">
              {t("Headless query execution for CI, MCP, and agentic workflows.")}
            </p>

            <div className="rounded-lg border border-border bg-surface-raised px-3 py-3">
              <div className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-zinc-500">
                {t("Run without installing")}
              </div>
              <div className="flex items-center justify-between gap-2 rounded bg-surface px-2.5 py-2 font-mono text-xs text-zinc-100 ring-1 ring-border">
                <span className="truncate">npx spuerhund --help</span>
                <button
                  type="button"
                  onClick={() => void handleCopy("npx spuerhund --help")}
                  className="rounded p-1 text-zinc-500 hover:bg-surface-raised hover:text-zinc-200"
                  title={t("Copy command")}
                >
                  <Copy size={12} />
                </button>
              </div>
            </div>
          </div>

          <div className="text-center text-[10px] text-zinc-600">
            <a
              href="https://spuerhund.dev/download"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-zinc-400"
            >
              {t("All install options on spuerhund.dev →")}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
